import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "@/components/ui";
import { apiClient } from "@/lib/api";

const DEBOUNCE_MS = 600;
const MIN_LOOKUP_LENGTH = 11;
const EU_VAT_PREFIX_RE = /^[A-Z]{2}.+/i;

type TaxIdentifierKind = "vat" | "fiscal_code";

interface TaxValidationResponse {
  valid?: boolean | null;
  format_checked?: boolean;
  name?: string | null;
  address?: string | null;
  reason?: string | null;
}

function isCompleteEnough(value: string): boolean {
  return value.trim().replace(/\s/g, "").length >= MIN_LOOKUP_LENGTH;
}

function compactAddress(address: string): string {
  return address.replace(/\s*\n+\s*/g, ", ").replace(/\s+/g, " ").trim();
}

export function useTaxIdentifierHint(value: string, kind: TaxIdentifierKind) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("settings");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestIdRef = useRef(0);
  const [checking, setChecking] = useState(false);
  const [hint, setHint] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const normalized = value.trim();
    if (!isCompleteEnough(normalized)) {
      setChecking(false);
      setHint(undefined);
      return;
    }

    if (kind === "vat" && !EU_VAT_PREFIX_RE.test(normalized.replace(/\s/g, ""))) {
      setChecking(false);
      setHint(undefined);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const requestId = ++requestIdRef.current;
      setChecking(true);
      setHint(undefined);

      void (async () => {
        try {
          const response = await apiClient.post<TaxValidationResponse>(
            "/tax-validation",
            { kind, value: normalized },
          );
          const result = response.data;
          if (requestId !== requestIdRef.current) return;

          if (kind === "vat") {
            if (result.valid && result.name) {
              const address = result.address ? compactAddress(result.address) : "";
              setHint(
                address
                  ? t("taxValidation.vatMatched", { name: result.name, address })
                  : result.name,
              );
              return;
            }
            if (
              result.reason === "invalid_format" ||
              result.reason === "not_registered" ||
              result.reason === "unrecognized_format"
            ) {
              setHint(t("taxValidation.vatNotFound"));
            }
            return;
          }

          if (result.format_checked && result.valid === false) {
            setHint(t("taxValidation.fiscalCodeInvalid"));
          }
        } catch {
          // Advisory only.
        } finally {
          if (requestId === requestIdRef.current) {
            setChecking(false);
          }
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, kind, t]);

  const rightIcon = checking ? (
    <Spinner size="sm" variant="muted"  loadingLabel={tCommon("loading")} />
  ) : undefined;

  return { hint, checking, rightIcon };
}
